import { useCallback, type MouseEvent } from "react";
import { useThemeMode } from "@contexts/ThemeContext";
import {
  isEmbedded as isEmbeddedHost,
  isTeamsEmbed,
} from "@/embed/embedHost";
import { openTeamsLink } from "@/embed/teamsHost";
import type {
  SignInGateViewModel,
  SignInGateViewModelArgs,
} from "./SignInGate.ViewModel.types";

/** The marketing site. Overridable so a self-hosted deployment can point elsewhere. */
const WEBSITE_URL =
  import.meta.env.VITE_WEBSITE_URL || "https://about.onetasklist.com";

export const useSignInGateViewModel = ({
  isSigningIn,
  hasFailed,
  popupBlocked,
  onSignIn,
}: SignInGateViewModelArgs): SignInGateViewModel => {
  const { themeMode } = useThemeMode();
  const isEmbedded = isEmbeddedHost();

  const handleSignIn = useCallback(() => {
    if (isSigningIn) {
      return;
    }
    onSignIn();
  }, [isSigningIn, onSignIn]);

  const handleWebsiteClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      // Outside Teams, and for the modified clicks people use to choose a
      // window for themselves, the anchor already does the right thing.
      if (
        !isTeamsEmbed() ||
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      void openTeamsLink(WEBSITE_URL).catch(() => {
        // The client could not be reached, so fall back to what the anchor
        // would have done rather than leaving the link dead.
        window.open(WEBSITE_URL, "_blank", "noopener,noreferrer");
      });
    },
    [],
  );

  return {
    isEmbedded,
    hasFailed,
    popupBlocked,
    isBusy: isSigningIn,
    // Only the embedded flows open a window; on the open web sign-in is a
    // redirect, where warning about a popup would just confuse. Once the
    // browser has actually refused one the headline says so, so the hint would
    // only repeat it.
    showsPopupHint: isEmbedded && !popupBlocked,
    // Light is the only theme with a near-white card, so it is the only one
    // that takes the dark-on-transparent logo. Same rule as the app header.
    logoSuffix: themeMode === "light" ? "" : "_inverted",
    websiteUrl: WEBSITE_URL,
    handleSignIn,
    handleWebsiteClick,
  };
};
